import { useEffect } from 'react';
import clsx from 'clsx';
import { useSearchParams } from 'react-router-dom';
import { useInboxStore } from '../stores/inbox-store';
import {
  useInboxDrawerStore,
  parseDrawerParam,
  encodeDrawerParam,
} from '../stores/inbox-drawer-store';
import { ThreadList } from '../components/inbox/ThreadList';
import { ThreadDetail } from '../components/inbox/ThreadDetail';
import { KlantinfoSlideOut } from '../components/inbox/KlantinfoSlideOut';

/**
 * Inbox V3, Gmail-indeling (ronde 11, 14-09): de lijst op volle breedte als
 * werklijst; open je een gesprek, dan neemt ThreadDetail de hele breedte
 * over met de klantkaart als rail rechts. De lijst blijft gemount (hidden)
 * zodat scroll, selectie en paginering bewaard blijven; Esc of de pijl gaat
 * terug. Zelfde URL-contract als InboxPage (?thread=, ?open=). Verschil zit
 * in twee store-vlaggen: lezerModus (concepten en kaarten van de lezer, Opnieuw
 * via /api/inbox/lees) en klantLater (gesprek eerst, klantkaart erachteraan).
 * Plan: plans/2026-09-14-inbox-v3-v1-scherm-plus-kaarten.md
 */
export function InboxV3Page() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeThreadId = useInboxStore((s) => s.activeThread?.conversation.id ?? null);
  const isLoadingThread = useInboxStore((s) => s.isLoadingThread);
  const setActiveThread = useInboxStore((s) => s.setActiveThread);
  const closeThread = useInboxStore((s) => s.closeThread);
  const setKlantLater = useInboxStore((s) => s.setKlantLater);
  const setLezerModus = useInboxStore((s) => s.setLezerModus);

  const drawerOpenItem = useInboxDrawerStore((s) => s.openItem);
  const openDrawer = useInboxDrawerStore((s) => s.open);
  const closeDrawer = useInboxDrawerStore((s) => s.close);

  useEffect(() => {
    setLezerModus(true);
    setKlantLater(true);
    return () => { setLezerModus(false); setKlantLater(false); };
  }, [setLezerModus, setKlantLater]);

  const threadParam = searchParams.get('thread');
  useEffect(() => {
    if (threadParam && threadParam !== activeThreadId) {
      void setActiveThread(threadParam);
    } else if (!threadParam && activeThreadId) {
      closeThread();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadParam]);

  useEffect(() => {
    const current = searchParams.get('thread');
    if (current === activeThreadId) return;
    const next = new URLSearchParams(searchParams);
    if (activeThreadId) next.set('thread', activeThreadId);
    else next.delete('thread');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeThreadId]);

  const openParam = searchParams.get('open');
  useEffect(() => {
    const parsed = parseDrawerParam(openParam);
    const current = useInboxDrawerStore.getState().openItem;
    const same = parsed && current && parsed.type === current.type && parsed.id === current.id;
    if (parsed && !same) {
      openDrawer(parsed.type, parsed.id);
    } else if (!parsed && current) {
      closeDrawer();
    }
  }, [openParam, openDrawer, closeDrawer]);

  useEffect(() => {
    const desired = encodeDrawerParam(drawerOpenItem);
    const current = searchParams.get('open');
    if (desired === current) return;
    const next = new URLSearchParams(searchParams);
    if (desired) next.set('open', desired);
    else next.delete('open');
    setSearchParams(next, { replace: true });
  }, [drawerOpenItem, searchParams, setSearchParams]);

  useEffect(() => {
    return () => { closeThread(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const gesprekOpen = Boolean(activeThreadId) || isLoadingThread;

  return (
    <>
      <div className="flex-1 flex overflow-hidden">
        <div className={clsx('flex-1 flex flex-col bg-white min-w-0', gesprekOpen && 'hidden')}>
          <ThreadList variant="v3" />
        </div>
        {gesprekOpen && <ThreadDetail variant="v3" />}
      </div>
      <KlantinfoSlideOut />
    </>
  );
}
